import { useContext, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AxiosResponse } from 'axios';
import clsx from 'clsx';

import { SimpleProgressBar } from '@/components/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Button, Label, LABEL_TYPE } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext, RolesAndPermissionContext } from '@/contexts';
import { ORGANIZATION_PERMISSION_TYPE, PERMISSION_TYPE, UserType } from '@/enums';
import { TABS } from '@/enums/OrganizationTabsEnum';
import { bytesToGigabytes, showToast, ToastType } from '@/helpers';
import { formatOptionalDate, getDaysBetweenDates } from '@/helpers/date';
import {
  CalendarIcon,
  EditPenIcon,
  OrganizationsIcon as OrganizationIcon,
  TrashIcon
} from '@/resources';
import {
  useDeleteOrganizationMutation,
  useExportEventLogsMutation,
  useGetAllEventTypes,
  useGetAllUsers,
  useGetOrganizationByIdQuery
} from '@/services';
import { OrganizationPermission } from '@/types';

import { OrganizationBrandingTab } from './OrganizationBrandingTab';
import { CustomResolutionForm } from './components/CustomResolutionForm';
import { DetailsRow } from './components/DetailsRow';

export const OrganizationDetails = () => {
  const [t] = useTranslation('common');
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { currentUserData: currentUser, hasPermission } = useContext(AuthContext);
  const { roles } = useContext(RolesAndPermissionContext);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const [error, setError] = useState<number>();

  const [exportStartDate, setExportStartDate] = useState<Date | null>(null);
  const [exportEndDate, setExportEndDate] = useState<Date | null>(null);

  const [currentTab, setCurrentTab] = useState<string>(TABS.DETAILS);
  const hasBrandingAccess = hasPermission(PERMISSION_TYPE.CHANGE_ORGANIZATION_BRANDING);
  const visibleTabs = hasBrandingAccess ? Object.values(TABS) : [TABS.DETAILS];

  const creatorId = roles.find(
    (r) =>
      r.permissions?.includes(PERMISSION_TYPE.ORG_ACCESS) &&
      !r.permissions?.includes(PERMISSION_TYPE.GLOBAL_ACCESS)
  )?.id;
  const isCreator = currentUser?.role.name === UserType.Creator;

  const { data: organization, isLoading: isOrganizationLoading } = useGetOrganizationByIdQuery(
    Number(organizationId),
    {
      onSuccess: () => {
        undefined;
      },
      onError: (error) => {
        setError(error.status);
      }
    }
  );

  const { mutate: deleteOrganization } = useDeleteOrganizationMutation(queryClient, {
    onSuccess: () => {
      navigate('/organizations');
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const { data: allEventTypes } = useGetAllEventTypes();

  const { mutate: exportEventLogs, isLoading: isExporting } = useExportEventLogsMutation({
    onSuccess: (response) => {
      downloadExportCsv(response);
    },
    onError: () => {
      showToast(ToastType.ERROR, t('exportFailed'));
    }
  });

  const downloadExportCsv = (response?: AxiosResponse) => {
    const blob = new Blob([response?.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response?.headers['content-disposition'] as string | undefined;
    const fileNameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    link.download = fileNameMatch?.[1]?.replace(/['"]/g, '') ?? 'export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const shouldFetchCreators = !isCreator && !isOrganizationLoading && !!creatorId;

  const { isLoading, data: allUsers } = useGetAllUsers(
    0,
    '',
    creatorId,
    Number(organizationId),
    true,
    shouldFetchCreators,
    true
  );

  const eventTypeNames =
    organization?.data?.eventTypes
      ?.map(
        (id: number) =>
          allEventTypes?.data?.items?.find((eventType: { id: number }) => eventType.id === id)?.name
      )
      .filter(Boolean)
      .map(t)
      .join(', ') || '-';

  const creatorUsers = allUsers?.pages.flatMap((page) => page.data?.items) ?? [];
  const organizationSettings = organization?.data?.organizationSettings;
  const hasTributeVideoLimit =
    organizationSettings?.tributeVideoExportsLimit !== null &&
    organizationSettings?.tributeVideoExportsLimit !== undefined;
  const hasStorageLimit =
    organizationSettings?.storageLimit !== null &&
    organizationSettings?.storageLimit !== undefined &&
    organizationSettings?.storageLimit !== 0;

  const canChangeCustomResolution =
    hasPermission(PERMISSION_TYPE.CHANGE_CUSTOM_RESOLUTION) &&
    (organization?.data?.paymentPlan?.organizationPermissions ?? []).some(
      (permission: OrganizationPermission) =>
        permission.name === ORGANIZATION_PERMISSION_TYPE.CUSTOM_VIDEO_RESOLUTION
    );

  const hasValidPaymentInterval =
    organizationSettings?.paymentIntervalStartDate &&
    (() => {
      const date = new Date(organizationSettings.paymentIntervalStartDate);
      return !isNaN(date.getTime()) && date.getFullYear() > 1;
    })();

  const handleExportCeremonies = () => {
    if (!exportStartDate || !exportEndDate) {
      return;
    }

    exportEventLogs({
      orgId: Number(organizationId),
      startDate: exportStartDate,
      endDate: exportEndDate
    });
  };

  const handleEditOrganization = () => {
    navigate(`/organizations/${organizationId}/edit`);
  };

  const headerButtonsConfig: ButtonConfig[] = [
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditOrganization,
      permissions: [PERMISSION_TYPE.UPDATE_ORGANIZATION]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: () => setIsOpenDeleteModal(true),
      permissions: [PERMISSION_TYPE.DELETE_ORGANIZATION]
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <div className='overflow-auto'>
      <DetailsPageLayout
        icon={<OrganizationIcon className='stroke-[0.5] text-lg text-primary-blue' />}
        iconPath={'/organizations'}
        path={[{ label: t('organizationDetails') }]}
        title={`${organization?.data.name}`}
        tag={organization?.data?.enabled ? 'Active' : 'Inactive'}
        isLoading={isOrganizationLoading || (shouldFetchCreators && isLoading)}
        headerButtons={headerButtonsConfig}>
        {hasBrandingAccess && (
          <div className='no-scrollbar flex flex-row overflow-x-scroll lg:overflow-x-hidden'>
            {visibleTabs.map((tab) => (
              <div
                key={tab}
                className={clsx(
                  'cursor-pointer whitespace-nowrap rounded px-5 py-2.5',
                  currentTab === tab && 'bg-secondary-pale-blue-gray font-bold'
                )}
                onClick={() => setCurrentTab(tab)}>
                {t(tab)}
              </div>
            ))}
          </div>
        )}
        {hasBrandingAccess && <div className='w-full border-b' />}
        {currentTab === TABS.BRANDING && organizationId && (
          <OrganizationBrandingTab
            organizationId={Number(organizationId)}
            language={organization?.data?.language}
          />
        )}
        {currentTab === TABS.DETAILS && (
          <>
            <div className='mt-3 flex space-x-9 md:mt-12'>
              <div className='flex w-1/2 flex-col gap-4'>
                <DetailsRow label={t('eventType')} value={eventTypeNames} />
                <DetailsRow
                  label={t('numberOfUsers')}
                  value={organization?.data?.userIds?.length}
                />
                <DetailsRow
                  label={t('createdAt')}
                  value={formatOptionalDate(organization?.data?.createdAt)}
                />
                <DetailsRow
                  label={t('paymentPlan')}
                  value={organization?.data?.paymentPlan?.name ?? '-'}
                />
              </div>

              <div className='flex w-1/2 flex-col gap-4'>
                {hasValidPaymentInterval && (
                  <>
                    <DetailsRow
                      label={t('paymentType')}
                      value={t(organizationSettings?.intervalType)}
                      className='gap-4'
                    />
                    <DetailsRow
                      label={t('startDate')}
                      value={formatOptionalDate(organizationSettings?.paymentIntervalStartDate)}
                      className='gap-4'
                    />
                    <DetailsRow
                      label={t('noOfDays')}
                      value={getDaysBetweenDates(
                        organizationSettings?.paymentIntervalStartDate,
                        organizationSettings?.nextPaymentDate
                      )}
                      className='gap-4'
                    />
                    <DetailsRow
                      label={t('nextPayment')}
                      value={formatOptionalDate(organizationSettings?.nextPaymentDate)}
                      className='gap-4'
                    />
                    <div className='flex w-full flex-col gap-2'>
                      <Label
                        text={t('ceremoniesUsedSummary', {
                          used: organizationSettings?.currentCeremoniesCount ?? 0,
                          total: organizationSettings?.ceremoniesPerInterval ?? 0
                        })}
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='pb-2.5 text-secondary-gray sm:p-0'
                      />
                      <div className='w-full'>
                        <SimpleProgressBar
                          value={organizationSettings?.currentCeremoniesCount ?? 0}
                          max={organizationSettings?.ceremoniesPerInterval ?? 0}
                        />
                      </div>
                    </div>
                  </>
                )}
                {hasTributeVideoLimit && (
                  <div className='flex w-full flex-col gap-2'>
                    <Label
                      text={t('tributeVideoExportsUsedSummary', {
                        used: organizationSettings?.tributeVideoExportsCount ?? 0,
                        total: organizationSettings?.tributeVideoExportsLimit ?? 0
                      })}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='pb-2.5 text-secondary-gray sm:p-0'
                    />
                    <div className='w-full'>
                      <SimpleProgressBar
                        value={organizationSettings?.tributeVideoExportsCount ?? 0}
                        max={organizationSettings?.tributeVideoExportsLimit ?? 0}
                      />
                    </div>
                  </div>
                )}
                {hasStorageLimit && (
                  <div className='flex w-full flex-col gap-2'>
                    <Label
                      text={t('storageUsedSummary', {
                        used: bytesToGigabytes(organizationSettings?.storageUsed ?? 0) || 0,
                        total: bytesToGigabytes(organizationSettings?.storageLimit ?? 0) || 0
                      })}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='pb-2.5 text-secondary-gray sm:p-0'
                    />
                    <div className='w-full'>
                      <SimpleProgressBar
                        value={organizationSettings?.storageUsed ?? 0}
                        max={organizationSettings?.storageLimit ?? 0}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {canChangeCustomResolution && (
              <>
                <div className='my-9 border-t border-secondary-blue-gray'></div>
                <CustomResolutionForm
                  organizationId={Number(organizationId)}
                  customResolution={organizationSettings?.customResolution}
                />
              </>
            )}
            {!isCreator && hasPermission(PERMISSION_TYPE.VIEW_EVENT_LOGS) && (
              <>
                <div className='my-9 border-t border-secondary-blue-gray'></div>
                <div className='mb-6'>
                  <div className='mb-4 flex items-center'>
                    <Label text={`${t('exportCeremonies')}`} type={LABEL_TYPE.H3_BOLD} />
                  </div>
                  <div className='flex flex-col gap-4 sm:flex-row sm:items-end'>
                    <div className='flex flex-col gap-2'>
                      <Label text={t('startDate')} type={LABEL_TYPE.PARAGRAPH2} />
                      <div className='w-48'>
                        <DatePicker
                          wrapperClassName='w-full'
                          selected={exportStartDate}
                          onChange={(date) => setExportStartDate(date)}
                          selectsStart
                          startDate={exportStartDate}
                          endDate={exportEndDate}
                          popperClassName='!z-50'
                          customInput={
                            <div className='flex h-10 w-full cursor-pointer items-center rounded-[10px] border border-solid border-secondary-blue-gray hover:border-primary-blue'>
                              <span
                                className={`ml-3 flex-1 text-sm ${exportStartDate ? 'text-primary-black' : 'text-secondary-gray'}`}>
                                {exportStartDate
                                  ? exportStartDate.toLocaleDateString('en-GB')
                                  : t('startDate')}
                              </span>
                              <CalendarIcon className='mr-3 shrink-0' />
                            </div>
                          }
                          popperPlacement='bottom-start'
                        />
                      </div>
                    </div>
                    <div className='flex flex-col gap-2'>
                      <Label text={t('endDate')} type={LABEL_TYPE.PARAGRAPH2} />
                      <div className='w-48'>
                        <DatePicker
                          wrapperClassName='w-full'
                          selected={exportEndDate}
                          onChange={(date) => setExportEndDate(date)}
                          selectsEnd
                          startDate={exportStartDate}
                          endDate={exportEndDate}
                          minDate={exportStartDate ?? undefined}
                          popperClassName='!z-50'
                          customInput={
                            <div className='flex h-10 w-full cursor-pointer items-center rounded-[10px] border border-solid border-secondary-blue-gray hover:border-primary-blue'>
                              <span
                                className={`ml-3 flex-1 text-sm ${exportEndDate ? 'text-primary-black' : 'text-secondary-gray'}`}>
                                {exportEndDate
                                  ? exportEndDate.toLocaleDateString('en-GB')
                                  : t('endDate')}
                              </span>
                              <CalendarIcon className='mr-3 shrink-0' />
                            </div>
                          }
                          popperPlacement='bottom-start'
                        />
                      </div>
                    </div>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      disabled={!exportStartDate || !exportEndDate || isExporting}
                      handleOnClick={handleExportCeremonies}>
                      {t('export')}
                    </Button>
                  </div>
                </div>
              </>
            )}
            {!isCreator && (
              <>
                <div className='my-9 border-t border-secondary-blue-gray'></div>
                <div className='mb-4 flex items-center'>
                  <Label text={`${t('creators')}`} type={LABEL_TYPE.H3_BOLD} />
                </div>
                <div className='custom-scrollbar z-10 flex max-h-[45vh] flex-col gap-y-4 overflow-y-auto px-2 md:px-0 3xl:max-h-[50vh] 4xl:max-h-[55vh]'>
                  {creatorUsers?.map((creator, index) => (
                    <div className='flex items-center' key={index}>
                      <div className='flex flex-col md:flex-row md:items-center md:gap-x-4'>
                        <div className='flex gap-2 sm:flex-none'>
                          <Label text={`${index + 1}.`} type={LABEL_TYPE.PARAGRAPH1} />

                          <Label
                            text={`${creator.firstName} ${creator.lastName} `}
                            type={LABEL_TYPE.PARAGRAPH1}
                            className='!text-primary-black'
                          />
                        </div>
                        <Label
                          text={`${creator.email}`}
                          type={LABEL_TYPE.PARAGRAPH1}
                          className='!text-primary-black'
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DetailsPageLayout>
      {isOpenDeleteModal && (
        <ConfirmationModal
          onConfirm={() => deleteOrganization(organization?.data?.id)}
          isOpen={isOpenDeleteModal}
          onClose={() => setIsOpenDeleteModal(false)}
          message={t('deleteMessage', { item: organization?.data.name })}
        />
      )}
    </div>
  );
};
